import React, { useEffect } from 'react';
import { io } from 'socket.io-client';

import { absURL } from '../utils';

const STORAGE_PARTICIPANT_ID = 'courseSessionParticipantId';
const STORAGE_ALIAS = 'courseSessionAlias';
const STORAGE_SESSION_SHORT_ID = 'courseSessionShortId';
const PENDING_ALIAS_KEY = 'pendingCourseSessionAlias';

/**
 * Root-level provider that keeps a single Socket.IO connection alive for the
 * whole "kurstillfälle" (course session), independent of which quiz route the
 * participant is currently on. When the lecturer starts the next quiz in the
 * group, it pushes the participant there automatically — no new QR scan, no
 * new name entry.
 *
 * A no-op for anyone who hasn't joined a course session (the common case):
 * no stored participant id means no socket is opened at all.
 */
export default function CourseSessionProvider({ children }) {

  useEffect(() => {

    let participantId;
    try {
      participantId = localStorage.getItem(STORAGE_PARTICIPANT_ID);
    } catch (e) {
      return;
    }

    if (!participantId) {
      return;
    }

    const socket = io('/', {
      path: '/socketIO',
      query: { csid: participantId }
    });

    const onQuizStarted = (msg) => {

      // This fires both for a live push and for the catch-up sent on every
      // (re)connect (see socketIOPlugin.js) — a reconnect while already on
      // the current quiz's page must be a no-op, or a flaky connection would
      // reload the page in a loop for no reason.
      const targetPath = absURL(`/${msg.quizAccessShortId}`);
      if (window.location.pathname === targetPath) {
        return;
      }

      try {
        sessionStorage.setItem(PENDING_ALIAS_KEY, localStorage.getItem(STORAGE_ALIAS) || '');
      } catch (e) {
        // Ignore storage errors — worst case the participant sees the normal join form.
      }

      // A real navigation, not wouter's setLocation: every quiz push matches
      // the same "/:quizAccessShortId" route pattern, so a client-side route
      // change wouldn't remount the page — the participant would stay stuck
      // on the previous quiz until they manually reloaded. A full navigation
      // guarantees a fresh mount every time, picking up the new quiz and the
      // pending-alias handoff above.
      window.location.assign(targetPath);
    };

    socket.on('courseSessionQuizStarted', onQuizStarted);

    return () => {
      socket.off('courseSessionQuizStarted', onQuizStarted);
      socket.disconnect();
    };

  }, []);

  return <>{children}</>;
}

export { STORAGE_PARTICIPANT_ID, STORAGE_ALIAS, STORAGE_SESSION_SHORT_ID, PENDING_ALIAS_KEY };
